import { Layer } from "bernie-view";
import { UitkDialogAnimationType, UitkFullscreenDialog } from "uitk-react-dialog";
import { ToolbarType, UitkToolbar } from "uitk-react-toolbar";
import { PROPERTY_FILTERS } from "components/flexComponents/PropertyFilters/PropertyFilters";
import { QueryRoute, RouteStore } from "bernie-core";
import * as React from "react";
import { useLocalization } from "@shared-ui/localization-context";
import { FlexClickTracker } from "components/utility/analytics/FlexClickTracker/FlexClickTracker";
import { UitkPrimaryButton, UitkTertiaryButton } from "uitk-react-button";
import { withStores } from "stores";
import { PropertyFiltersGroupProps } from "components/flexComponents/PropertyFilters/typings";
import { PROPERTY_FILTERS_COMPONENT_MAP } from "components/shared/PropertyFIlterComponents/map/PropertyFiltersComponentMap";
import { PROPERTY_FILTERS_COMPONENT_WRAPPER_MAP } from "components/shared/PropertyFIlterComponents/map/PropertyFiltersComponentWrapperMap";
import {
  Action,
  FlexTrackingInfo,
  sendImmediateClickstreamTrackEvent,
} from "components/utility/analytics/FlexAnalyticsUtils";
import { useClickTracker } from "@shared-ui/clickstream-analytics-context";
import { PropertyFiltersStore } from "src/stores/PropertyFiltersStore";
import { observer } from "mobx-react";

const ClearButtonTracked = FlexClickTracker(UitkTertiaryButton);

export const PropertyFiltersDialog = withStores(
  "routeStore",
  "propertyFilters"
)(
  observer((props: { routeStore: RouteStore; children: React.ReactNode; propertyFilters: PropertyFiltersStore }) => {
    const { routeStore, propertyFilters } = props;
    const l10n = useLocalization();
    const track = useClickTracker();

    const onDismiss = () => {
      routeStore.goBack();
    };

    const onClickClear = () => {
      propertyFilters.clearAllFilters();
    };

    const onSubmit = () => {
      const flexTracking: FlexTrackingInfo = {
        moduleName: PROPERTY_FILTERS,
        rfrr: `dialog.done.${propertyFilters.getNumberOfSelectedFilters()}`,
        action: Action.CLICK,
      };

      sendImmediateClickstreamTrackEvent(flexTracking, track);
      window.location.assign(propertyFilters.buildLinkToSRP());
    };

    return (
      <QueryRoute query={{ pwaDialog: "filters" }}>
        <Layer id="filters" onDismiss={onDismiss}>
          <UitkFullscreenDialog dialogShow={true} animation={UitkDialogAnimationType.SLIDEUP}>
            <UitkToolbar
              iconLabel={l10n.formatText("typeahead.cancel")}
              header={l10n.formatText("propertyfilters.filter")}
              type={ToolbarType.CLOSE}
              action={onDismiss}
              actionContent={
                <ClearButtonTracked
                  moduleName={PROPERTY_FILTERS}
                  rfrr="dialog.clear"
                  onClick={onClickClear}
                  className="uitk-type-400"
                  text={l10n.formatText("typeahead.templates.clear")}
                />
              }
            />

            {props.children}
            <div className=" all-y-padding-two all-x-padding-two">
              <UitkPrimaryButton
                text={l10n.formatText("datePicker.doneHeader")}
                onClick={onSubmit}
                isFullWidth={true}
              />
            </div>
          </UitkFullscreenDialog>
        </Layer>
      </QueryRoute>
    );
  })
);

/**
 * Represents a filter group (e.g. Popular Filters) viewed within a Dialog.
 * @param props
 * @constructor
 */
export const PropertyFilterInDialog = (props: PropertyFiltersGroupProps) => {
  const { filter, withoutHeader } = props;

  if (!filter) {
    return null;
  }

  const { name, options } = filter;

  const Component = PROPERTY_FILTERS_COMPONENT_MAP[filter.name];
  const Wrapper = PROPERTY_FILTERS_COMPONENT_WRAPPER_MAP[filter.name] || React.Fragment;

  return (
    <fieldset>
      {!withoutHeader && <legend className="all-b-padding-four uitk-type-500 uitk-type-bold">{filter?.label}</legend>}
      <Wrapper>
        {Array.from(options.values()).map((option, index) => (
          <Component
            key={`${option.optionValue}-${index}`}
            option={option}
            filterName={name}
            moduleName="PropertyFilters"
          />
        ))}
      </Wrapper>
    </fieldset>
  );
};
